import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../Controllers/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../Controllers/Store";
import { hasPermission } from "../../Controllers/Selectors/permissionsSelector";

import { Ripple } from "primereact/ripple";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { Tooltip } from "primereact/tooltip";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

import NoDataCreated from "../../Components/NoDataCreated";
import PageLoader from "../../Components/PageLoader";
import ErrorPage from "../../Components/ErrorPage";
import SharedByAvatar from "../../Components/SharedByAvatar";
import { goToTop } from "../../Components/goToTop";

import { fetchFSProjectList } from "../../Controllers/Thunks/FinishingScheduleThunks";
import { FSProject } from "../../Controllers/Types/FinishingSchedule/FinishingSchedule";
import { addProjectForFinishingSchedule } from "../../Api/FinishingScheduleService/FinishingScheduleService";
import TabFilter from "../../Components/TabFilter";

const FinishingScheduleProjects: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const toast = useRef<Toast>(null);

    const loadingFSProjects = useAppSelector((state) => state?.finishingSchedule?.loadingFSProjects);
    const errorFSProjects = useAppSelector((state) => state?.finishingSchedule?.errorFSProjects);
    const projectsList = useAppSelector((state) => state?.finishingSchedule?.fsProjects);

    const [loading, setLoading] = useState<boolean>(false);
    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);

    const projectTabs = ['Current', 'Ongoing', 'Completed'] as const;
    type ProjectTab = typeof projectTabs[number];
    const [projectsFilterState, setProjectsFilterState] = useState<"Current" | "Ongoing" | "Completed">("Current");
    const [finishingScheduleProjectsData, setFinishingScheduleProjectsData] = useState<FSProject[]>([]);
    const [filteredFinishingScheduleProjectsData, setFilteredFinishingScheduleProjectsData] = useState<FSProject[]>([]);

    const [projectsData, setProjectsData] = useState<FSProject[]>([]);
    const [showAddProjectsModal, setShowAddProjectsModal] = useState<boolean>(false);
    const [selectedProjects, setSelectedProjects] = useState<FSProject[]>([]);

    // PERMISION HANDLER
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const hasPermissionForAddProject = useCanEdit("Finishing Schedule", "add_project");

    useEffect(() => {
        finishingScheduleProjectDataGet();
    }, [dispatch]);

    const finishingScheduleProjectDataGet = async () => {
        await dispatch(fetchFSProjectList());
        setInitialDataLoading(false);
    }

    useEffect(() => {
        setFinishingScheduleProjectsData(projectsList.filter((e) => e?.is_fs_project_list === true));
        setProjectsData(projectsList.filter((e) => e?.is_fs_project_list === false));
    }, [projectsList]);

    useEffect(() => {
        const fetchFilteredData = async () => {
            if (projectsFilterState === "Current") {
                const currentProjects = finishingScheduleProjectsData?.filter(
                    (project) => project?.status === "Current"
                );
                setFilteredFinishingScheduleProjectsData(currentProjects);
            }

            if (projectsFilterState === "Ongoing") {
                const ongoingProjects = finishingScheduleProjectsData?.filter(
                    (project) => project?.status === "Ongoing"
                );
                setFilteredFinishingScheduleProjectsData(ongoingProjects);
            }

            if (projectsFilterState === "Completed") {
                const completedProjects = finishingScheduleProjectsData?.filter(
                    (project) => project?.status === "Completed"
                );
                setFilteredFinishingScheduleProjectsData(completedProjects);
            }
        };
        fetchFilteredData();
    }, [projectsFilterState, finishingScheduleProjectsData]);

    const handleFilterProjects = (filterState: ProjectTab) => {
        setProjectsFilterState(filterState);
        goToTop();
    };

    const tableActionBody = (rowData: FSProject) => {
        const projectData: FSProject = rowData;

        return (
            <div className="tabel_btn_area mt-1 mb-1">
                {(rowData.can_edit != null && rowData.can_edit === true) && (
                    <Button
                        icon={`bi bi-plus-circle`}
                        label={"Add"}
                        className="tabel_btn view_btn fixed_width m-1"
                        onClick={() => {
                            navigate(`/finishing-shedule`, { state: { projectData: projectData, openStatus: 1 } });
                            goToTop();
                        }}
                    />
                )}

                <Button
                    icon={`bi bi-eye`}
                    label={"View"}
                    className="tabel_btn view_btn fixed_width"
                    onClick={() => {
                        navigate(`/finishing-shedule`, { state: { projectData: projectData, openStatus: 0 } });
                        goToTop();
                    }}
                    disabled={(projectData && projectData?.feature_list?.length === 0)}
                />
            </div>
        );
    };

    /* ******************************* Add project ******************************* */
    const handleCloseProjectModal = () => {
        setShowAddProjectsModal(false);
        setSelectedProjects([]);
    };

    const handleAddProject = () => {
        setShowAddProjectsModal(true);
    };

    function checkCredentials(): boolean {
        return selectedProjects.length > 0;
    }

    const addProjects = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials()) {
            setLoading(true);
            const data = await addProjectForFinishingSchedule(selectedProjects);
            setLoading(false);

            if (data.statusCode === 200) {
                finishingScheduleProjectDataGet();
                handleCloseProjectModal();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Project(s) added successfully.",
                        life: 3000,
                    });
                }
            } else {
                setLoading(false);
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error adding the project.",
                        life: 3000,
                    });
                }
            }
        }
    };

    const projectModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">Add project(s)</h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseProjectModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const projectModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseProjectModal}
            />

            <Button
                label={`${loading ? "Processing" : "Add"}`}
                onClick={addProjects}
                loading={loading}
                className="custom_btn primary"
                disabled={selectedProjects?.length === 0}
            />
        </div>
    );

    const handleSelectProjects = (project: FSProject, isChecked: boolean) => {
        if (isChecked) {
            setSelectedProjects((prevState) => [...prevState, project]);
        } else {
            setSelectedProjects((prevState) =>
                prevState.filter(
                    (existingProject) => existingProject.project_id !== project.project_id
                )
            );
        }
    };

    const sharedContactBody = (rowData: FSProject) => {
        return (
            <React.Fragment>
                {(rowData.shared_by != null) ? (
                    <SharedByAvatar
                        avatarKey={rowData?.shared_by?.id}
                        firstName={rowData?.shared_by?.first_name}
                        lastName={rowData?.shared_by?.last_name}
                        profileImage={rowData?.shared_by?.profile_image}
                    />
                ) : (
                    <span>---</span>
                )}
            </React.Fragment>
        );
    };

    const isSelectedProject = (project: FSProject) =>
        selectedProjects.some(
            (existingproject) => existingproject?.project_id === project?.project_id
        );
    /* *************************************************************************** */

    /* ***************************** Common function ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingFSProjects && initialDataLoading ? (
                    <PageLoader />
                ) : errorFSProjects ? (
                    <ErrorPage errorMessage={errorFSProjects} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="d-flex align-items-center justify-content-between">
                            <h4 className="main_heading">Finishing schedule <span className="text_empty">- Projects</span> </h4>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3">
                            <div className="contact_tabs">
                                {projectTabs?.map((tab) => (
                                    <TabFilter
                                        key={tab}
                                        label={tab}
                                        isActive={projectsFilterState === tab}
                                        onTabChange={() => handleFilterProjects(tab)}
                                    />
                                ))}
                            </div>

                            <div className="page_action_area">
                                {(hasPermissionForAddProject) && (
                                    <button
                                        className="add_data_link_btn p-ripple"
                                        onClick={handleAddProject}
                                    >
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add project</span>
                                        <Ripple />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="page_main_content_section p-0">
                            {filteredFinishingScheduleProjectsData && filteredFinishingScheduleProjectsData?.length > 0 ? (
                                <DataTable
                                    value={filteredFinishingScheduleProjectsData}
                                    paginator
                                    size="small"
                                    rows={10}
                                    emptyMessage="No project(s) found!"
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    tableStyle={{ minWidth: "50rem" }}
                                    paginatorDropdownAppendTo={"self"}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    scrollable
                                    loading={loadingFSProjects}
                                >
                                    <Column
                                        header="Job no."
                                        headerClassName="text_purple"
                                        field="job_number"
                                        body={(rowData: FSProject) => (
                                            <span className="text-capitalize text_bold">
                                                {rowData?.job_number ? rowData?.job_number : "---"}
                                            </span>
                                        )}
                                        style={{ width: "15%" }}
                                    ></Column>

                                    <Column
                                        header="Type"
                                        headerClassName="text_purple"
                                        field="project_type"
                                        body={(rowData: FSProject) => (
                                            <span className="text-capitalize">
                                                {rowData?.project_type === "CP" ? "Commercial" : "Domestic"}
                                            </span>
                                        )}
                                        style={{ width: "15%" }}
                                    ></Column>

                                    <Column
                                        header="Address"
                                        headerClassName="text_purple"
                                        field="address"
                                        body={(rowData: FSProject) => (
                                            <span className="text-capitalize">
                                                {rowData?.address ? rowData?.address : "---"}
                                            </span>
                                        )}
                                        style={{ width: "25%" }}
                                    ></Column>

                                    <Column
                                        header="Description"
                                        headerClassName="text_purple"
                                        field="description"
                                        className="first_letter_capitalized"
                                        body={(rowData: FSProject) => (
                                            <span>
                                                {rowData?.description ? `${rowData.description.slice(0, 60)}${rowData.description?.length > 60 ? '...' : ''}` : "---"}
                                            </span>
                                        )}
                                        style={{ width: "25%" }}
                                    ></Column>

                                    <Column
                                        body={sharedContactBody}
                                        header="Shared by"
                                        headerClassName="text_purple"
                                        className="text-center"
                                        alignHeader={'center'}
                                        style={{ width: "8%" }}
                                    ></Column>

                                    <Column
                                        body={tableActionBody}
                                        alignHeader="center"
                                        className="table_btn_area"
                                        frozen={window.innerWidth > 575}
                                        alignFrozen="right"
                                        style={{ width: "12%" }}
                                    ></Column>
                                </DataTable>
                            ) : (
                                <NoDataCreated message={`No project(s) have been added. ${hasPermissionForAddProject ? 'Click the Add button to add project(s).' : ''}`} />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Project selection modal */}
            <Dialog
                visible={showAddProjectsModal}
                headerClassName="custom_modal_header"
                header={projectModalHeader}
                footer={projectModalFooter}
                dismissableMask
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseProjectModal}
            >
                <div className="custom_modal_body">
                    <div className="p-1">
                        {projectsData && projectsData?.length > 0 ? (
                            <div className="row">
                                <div className="col-12">
                                    <label className={`custom_form_label mb-2`}>
                                        Select project(s)
                                    </label>

                                    {projectsData?.map((project) => (
                                        <div
                                            className="option_checkbox_area"
                                            key={project?.project_id}
                                        >
                                            <Checkbox
                                                onChange={(e) => handleSelectProjects(project, e.checked || false)}
                                                checked={isSelectedProject(project)}
                                            ></Checkbox>

                                            <Tooltip
                                                key={`project_type_${project?.project_id}`}
                                                id={`project_type_${project?.project_id}`}
                                                target={`.project_type_${project?.project_id}`}
                                            />
                                            <article
                                                className={`shared_contact_body selectable mb-0 ${isSelectedProject(project) && "selected"} p-ripple`}
                                                onClick={() => handleSelectProjects(project, !isSelectedProject(project))}
                                            >
                                                <h6 className="text-capitalize">
                                                    {project?.job_number}: {project?.address}
                                                </h6>
                                                <h6
                                                    className={`project_type_${project?.project_id}`}
                                                    data-pr-tooltip={project?.project_type === "CP" ? "Commercial project" : "Domestic project"}
                                                    data-pr-position="top"
                                                >
                                                    {project?.project_type}
                                                </h6>
                                                <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                            </article>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="no_records_found">
                                <i className="bi bi-exclamation-circle me-1"></i>
                                Project(s) not available.
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>
            {/*  */}
        </React.Fragment>
    )
}

export default FinishingScheduleProjects;